import { TEMP_MESSAGE_PREFIX } from "@frontend-shared/constants";
import {
  EMessageRole,
  EToolApprovalState,
  type IApplyIntegrationConnectPromptToMessageProps,
  type IApplyToolApprovalRespondedToMessageProps,
  type IChatHistoryPage,
  type IChatHistoryPagesContainer,
  type IClearMessageStreamErrorProps,
  type IHasMessageUuidInPagesProps,
  type IMessage,
  type IUpsertToolApprovalRequestToMessageProps,
} from "@frontend-shared/types";
import {
  appendMessageToolStep,
  cancelSearchProgressOnMessage,
  mapMessagesInChatHistory,
  mergeIntegrationConnectPrompts,
  mergeSkillActivations,
  mergeSkillScriptRuns,
  readSkillScriptRuns,
  resolveMessageToolCall,
  setMessageSkillScriptRuns,
  updateFirstPageInChatHistory,
  updateMessageInChatHistory,
} from "@frontend-shared/utils";
import { checkIsAwaitingGeneratedImage } from "@frontend-shared/utils/generated-image-delivery";
import type { InfiniteData } from "@tanstack/react-query";

import type {
  IApplyStreamErrorToDataParams,
  IApplyStreamErrorToDataResult,
} from "@/hooks/types";
import type { IApplySkillScriptRunToMessageProps } from "@/lib/skill-script-runs.types";
import type {
  IAppendMessageDeltaProps,
  IAppendMessageReasoningProps,
  IAppendMessageToChatHistoryDataProps,
  IApplyIntegrationConnectPromptToChatHistoryProps,
  IApplySkillActivationsToMessageProps,
  IClearMessageToolCallInPagesProps,
  IFindMessageInPagesProps,
  ISetMessageDeepSearchProgressProps,
  ISetMessageKnowledgeSourcesProps,
  ISetMessagePreparationProgressProps,
  ISetMessageStreamErrorProps,
  ISetMessageStreamFinishedProps,
  ISetMessageToolCallProps,
  ISetMessageWebSearchProgressProps,
  IUpdateChatHistoryMessageProps,
  IUpdateMessageInPagesProps,
} from "@/lib/types";

// NOTE: NSAI-5909 — the content-replacement rule and the search-progress cancel
// rule now live in `frontend-shared`; mobile applies byte-identical copies. This
// re-export keeps the call sites that import them from this module unchanged.
export { updateMessageContentInPages } from "@frontend-shared/utils";

export const findMessageInPages = (
  params: IFindMessageInPagesProps,
): IMessage | undefined => {
  const { pages, uuid } = params;
  for (const page of pages) {
    const message = page.data.find((m) => m.uuid === uuid);
    if (message) return message;
  }
  return undefined;
};

// NOTE: NSAI-5908 — the web binding of the shared copy-on-write helper: one
// structuredClone of the touched message, never of the transcript. Every
// streamed frame used to deep-copy the whole cache (base64 attachments
// included) and then have React Query deep-compare it all back; this rebuilds
// only the path to the message, so a frame costs what its message costs and
// every other row keeps its reference — which is what lets the transcript
// rows memoize (ChatHistoryMessage / AssistantCard / MessageContent).
export const updateChatHistoryMessage = <
  TData extends IChatHistoryPagesContainer,
>({
  oldData,
  uuid,
  mutate,
}: IUpdateChatHistoryMessageProps<TData>): TData =>
  updateMessageInChatHistory({
    oldData,
    uuid,
    mutate,
    cloneMessage: structuredClone,
  });

// NOTE: NSAI-5908 — drops the optimistic (temp-*) messages from the first
// (most recent) page — the only page they are ever prepended to — keeping
// every other message by reference. Returns `oldData` itself when there was
// nothing to drop, so callers can tell by identity whether anything changed.
export const removeTemporaryMessagesFromChatHistory = <
  TData extends IChatHistoryPagesContainer,
>(
  oldData: TData,
): TData =>
  updateFirstPageInChatHistory({
    oldData,
    update: (data) => {
      const kept = data.filter(
        (message) => !message.uuid.startsWith(TEMP_MESSAGE_PREFIX),
      );
      return kept.length === data.length ? null : kept;
    },
  });

export const updateMessageInPages = (
  params: IUpdateMessageInPagesProps,
): boolean => {
  const { pages, message } = params;
  for (const page of pages) {
    const messageIndex = page.data.findIndex(
      (m: IMessage) => m.uuid === (message.tempId || message.uuid),
    );
    if (messageIndex !== -1) {
      const existing = page.data[messageIndex];
      const mergedMessage = {
        ...existing,
        ...message,
      };
      // NOTE: contextChatSources (NSAI-3501) arrives on the separate
      // `knowledgeSources` websocket event and is persisted asynchronously, but
      // its field name matches the message entity column — so a finalize
      // `messageUpdated` can carry it as null (read before the async persist
      // commits) and clobber the value already shown in the UI. Preserve the
      // cached value when the incoming update doesn't carry one.
      if (message.contextChatSources == null && existing.contextChatSources) {
        mergedMessage.contextChatSources = existing.contextChatSources;
      }
      // NOTE: If streaming resumes after a transient disconnect, clear interruption flags.
      if (!mergedMessage.done) {
        delete mergedMessage.streamError;
        mergedMessage.streamFinished = false;
      }
      page.data[messageIndex] = mergedMessage;
      return true;
    }
  }
  return false;
};

export const appendMessageDelta = (
  params: IAppendMessageDeltaProps,
): boolean => {
  const { pages, uuid, delta } = params;
  for (const page of pages) {
    const message = page.data.find((msg: IMessage) => msg.uuid === uuid);
    if (message) {
      // NOTE: Never append streamed deltas into a completed message.
      // If the message is done, DB content is final and should remain stable.
      if (message.done) {
        return false;
      }

      message.content += delta;
      // NOTE: Streaming deltas should not "un-finish" a message after refresh.
      message.done = false;
      // NOTE: Incoming stream content means generation is active again.
      delete message.streamError;
      message.streamFinished = false;

      return true;
    }
  }
  return false;
};

export const setMessageToolCall = (
  params: ISetMessageToolCallProps,
): boolean => {
  const { pages, uuid, toolName, args, toolCallId } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      message.tool = resolveMessageToolCall({
        toolName,
        args,
        priorTool: message.tool,
        toolCallId,
      });
      if (toolCallId) {
        appendMessageToolStep({ message, toolCallId, toolName, args });
      }
      return true;
    }
  }
  return false;
};

export const appendMessageReasoning = (
  params: IAppendMessageReasoningProps,
): boolean => {
  const { pages, uuid, reasoning } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      // NOTE: Never append streamed reasoning into a completed message.
      // After a stop, the message is closed (done=true) while late reasoning
      // deltas can still arrive from the aborted stream — dropping them keeps
      // the cancelled message stable, matching appendMessageDelta above.
      if (message.done) {
        return false;
      }

      message.reasoning = (message.reasoning || "") + reasoning;
      return true;
    }
  }
  return false;
};

export const setMessageStreamError = (
  params: ISetMessageStreamErrorProps,
): boolean => {
  const { pages, uuid, errorMessage } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      message.streamError = errorMessage;
      return true;
    }
  }
  return false;
};

export const clearMessageStreamError = (
  params: IClearMessageStreamErrorProps,
): boolean => {
  const { pages, uuid } = params;
  for (const page of pages) {
    const message = page.data.find((msg: IMessage) => msg.uuid === uuid);
    if (message) {
      // NOTE: streamError is transient; once we have any terminal update (done/finish),
      // we should not keep it, otherwise UI may hide generated content.
      delete message.streamError;
      return true;
    }
  }
  return false;
};

export const setMessageStreamFinished = (
  params: ISetMessageStreamFinishedProps,
): boolean => {
  const { pages, uuid } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      message.streamFinished = true;
      return true;
    }
  }
  return false;
};

export const setMessageKnowledgeSources = (
  params: ISetMessageKnowledgeSourcesProps,
): boolean => {
  const { pages, uuid, sources, contextChatSources } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      if (sources !== undefined) {
        message.knowledgeSources = sources;
      }
      // NOTE: Cross-chat context provenance (NSAI-3501) — set alongside
      // knowledge sources so the ContextChatSources block renders live.
      if (contextChatSources !== undefined) {
        message.contextChatSources = contextChatSources;
      }
      return true;
    }
  }
  return false;
};

export const setMessageDeepSearchProgress = (
  params: ISetMessageDeepSearchProgressProps,
): boolean => {
  const { pages, uuid, progress, researchProgress } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      message.deepSearchProgress = progress;
      // NOTE: NSAI-6435 — a v2 event carries the run's record; an event
      // without one keeps the record the message already has.
      if (researchProgress) {
        message.deepResearchProgress = researchProgress;
      }
      return true;
    }
  }
  return false;
};

export const setMessageWebSearchProgress = (
  params: ISetMessageWebSearchProgressProps,
): boolean => {
  const { pages, uuid, progress } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      message.webSearchProgress = progress;
      return true;
    }
  }
  return false;
};

export const setMessagePreparationProgress = (
  params: ISetMessagePreparationProgressProps,
): boolean => {
  const { pages, uuid, progress } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      message.preparationProgress = progress;
      return true;
    }
  }
  return false;
};

export const checkHasRequestedToolApproval = (message: IMessage): boolean =>
  message.toolApprovals?.some(
    (approval) => approval.toolApproval.state === EToolApprovalState.REQUESTED,
  ) ?? false;

export const checkIsMessageStreaming = (message: IMessage): boolean => {
  return (
    !message.done &&
    !message.streamError &&
    !message.streamFinished &&
    !checkHasRequestedToolApproval(message)
  );
};

export const hasActiveStreamInPages = (pages: IChatHistoryPage[]): boolean => {
  return pages.some((page) =>
    page.data.some(
      (msg) => msg.role === "assistant" && checkIsMessageStreaming(msg),
    ),
  );
};

// NOTE: NSAI-5042 — like hasActiveStreamInPages but ignoring temp-* optimistic
// placeholders: a temp assistant is a hope, not a stream. The auth-reconnect
// deferral must use THIS variant — counting a temp let a hung send indefinitely
// defer the very reconnect that would have delivered it, because the terminal
// events that clear the deferral never arrive for a message the server never
// created. Background-tracker idle checks keep using hasActiveStreamInPages so
// late newMessages for temps are still caught.
export const hasActiveServerStreamInPages = (
  pages: IChatHistoryPage[],
): boolean => {
  return pages.some((page) =>
    page.data.some(
      (msg) =>
        msg.role === "assistant" &&
        !msg.uuid.startsWith(TEMP_MESSAGE_PREFIX) &&
        checkIsMessageStreaming(msg),
    ),
  );
};

// NOTE: Broader check for background tracking: treats both streaming messages
// AND messages with pending tool approvals as "active work". This prevents
// premature cleanup when an agent is waiting for user confirmation.
export const hasActiveWorkInPages = (pages: IChatHistoryPage[]): boolean => {
  return pages.some((page) =>
    page.data.some((msg) => {
      if (msg.role !== "assistant") return false;
      if (msg.done || msg.streamError || msg.streamFinished) return false;

      // NOTE: Message is either actively streaming or waiting for tool approval
      return true;
    }),
  );
};

// NOTE: Mark in-flight assistant messages as interrupted with targeted cloning.
// A turn paused on a tool approval is skipped (NSAI-7877): it is not streaming,
// and the server keeps its batch until the user answers or it auto-declines.
// Only clones pages/messages that need modification. Used when connection is lost.
export const markStreamingMessagesAsFinished = (params: {
  oldData: InfiniteData<IChatHistoryPage>;
  errorMessage: string;
}): InfiniteData<IChatHistoryPage> => {
  const { oldData, errorMessage } = params;
  let hasAnyUpdates = false;
  const newPages: IChatHistoryPage[] = [];

  for (const page of oldData.pages) {
    let hasPageUpdates = false;
    const newMessages: IMessage[] = [];

    for (const message of page.data) {
      // NOTE: A message that already finished streaming is left alone, with ONE
      // exception. Normally its answer is fully streamed by then and only the
      // `done` echo is outstanding, so stamping a streamError over a brief
      // reconnect blip would replace a complete answer with an error card until
      // the reconnect refetch healed it.
      //
      // The exception is a turn that still owes a generated image: it sits in
      // exactly that state — finished streaming, not yet done — while the
      // picture is persisted and base64-resolved, and the renderers now hold its
      // placeholder open instead of drawing "connection was lost". A socket that
      // really dies inside that window has to be the thing that ends it, or the
      // placeholder would wait forever for a `messageUpdated` that can no longer
      // arrive.
      // NOTE: NSAI-7877 — a turn paused on a tool approval is not streaming:
      // it waits on the user, and the server keeps the batch for the whole
      // auto-decline window (an hour by default). Over that long a wait an idle
      // or proxy socket drop is near-certain, and stamping the turn as lost
      // here made a later Approve / Deny land on a bubble already reading
      // "Connection was lost" instead of the continuation it resumes.
      const shouldMarkAssistantInterruptedOnDisconnect =
        message.role === EMessageRole.ASSISTANT &&
        !message.done &&
        !message.streamError &&
        !checkHasRequestedToolApproval(message) &&
        (!message.streamFinished || checkIsAwaitingGeneratedImage(message));

      if (shouldMarkAssistantInterruptedOnDisconnect) {
        // NOTE: Only clone messages that need to be updated
        newMessages.push({
          ...message,
          streamFinished: true,
          streamError: errorMessage,
          preparationProgress: undefined,
        });
        hasPageUpdates = true;
        hasAnyUpdates = true;
      } else {
        // NOTE: Reuse original message reference
        newMessages.push(message);
      }
    }

    // NOTE: Only create new page object if this page had updates
    newPages.push(hasPageUpdates ? { ...page, data: newMessages } : page);
  }

  // NOTE: Return original data if nothing changed to preserve reference equality
  if (!hasAnyUpdates) {
    return oldData;
  }

  return {
    ...oldData,
    pages: newPages,
  };
};

// NOTE: Clear the active tool-call signal on a message — used when the stream
// is cancelled so the parent UI stops rendering loaders like "Designing your
// slides..." for a tool that will never complete.
export const clearMessageToolCallInPages = (
  params: IClearMessageToolCallInPagesProps,
): boolean => {
  const { pages, uuid } = params;
  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (message) {
      message.tool = undefined;
      return true;
    }
  }
  return false;
};

// NOTE: In-place variant for the per-message `streamFinish` path, which
// already works on a cloned message.
export const cancelSearchProgressForMessageInPages = (params: {
  pages: IChatHistoryPage[];
  uuid: string;
}): boolean => {
  const { pages, uuid } = params;

  for (const page of pages) {
    const message = page.data.find((m: IMessage) => m.uuid === uuid);
    if (!message) continue;

    const cancelledMessage = cancelSearchProgressOnMessage(message);
    if (cancelledMessage) {
      Object.assign(message, cancelledMessage);
    }
    return true;
  }

  return false;
};

// NOTE: NSAI-5908 — chat-wide cancel on `messageStopped` that rebuilds only
// the pages holding a message with progress to cancel.
export const cancelSearchProgressForChat = <
  TData extends IChatHistoryPagesContainer,
>(
  oldData: TData,
): TData =>
  mapMessagesInChatHistory({ oldData, map: cancelSearchProgressOnMessage });

// NOTE: NSAI-3308 — merges rather than replaces, because the same records reach
// a client twice: once on the live `skillActivations` event and again when the
// chat history is refetched. `mergeSkillActivations` dedupes by `skillUuid` and
// orders by the server's `position`, so the timeline reads identically either way.
export const applySkillActivationsToMessage = ({
  pages,
  uuid,
  activations,
}: IApplySkillActivationsToMessageProps): boolean => {
  const message = findMessageInPages({ pages, uuid });
  if (!message) {
    return false;
  }

  message.skillActivations = mergeSkillActivations({
    existing: message.skillActivations,
    incoming: activations,
  });
  return true;
};

// NOTE: NSAI-3304 — one live `skillScriptRun` event carries ONE run record, and the
// history read returns the same records for the same message. `mergeSkillScriptRuns`
// dedupes by run `uuid` and orders by the server's `position`, so a redelivered event
// or a refetch updates the block in place instead of appending a twin.
export const applySkillScriptRunToMessage = ({
  pages,
  uuid,
  run,
}: IApplySkillScriptRunToMessageProps): boolean => {
  const message = findMessageInPages({ pages, uuid });
  if (!message) {
    return false;
  }

  setMessageSkillScriptRuns({
    message,
    runs: mergeSkillScriptRuns({
      existing: readSkillScriptRuns({ message }),
      incoming: [run],
    }),
  });
  return true;
};

// NOTE: NSAI-5681 — one live `integrationConnectPrompt` event carries ONE
// prompt, and the history read returns the same records for the message.
// `mergeIntegrationConnectPrompts` dedupes by provider, so a
// redelivered event or a refetch updates the card stack in place instead of
// appending a twin.
export const applyIntegrationConnectPromptToMessage = ({
  pages,
  uuid,
  prompt,
}: IApplyIntegrationConnectPromptToMessageProps): boolean => {
  const message = findMessageInPages({ pages, uuid });
  if (!message) {
    return false;
  }

  message.integrationConnectPrompts = mergeIntegrationConnectPrompts({
    existing: message.integrationConnectPrompts,
    incoming: [prompt],
  });
  return true;
};

// NOTE: NSAI-5681 — puts the transcript line a resolved connect card wrote at
// the end of the conversation, where a history read would have returned it.
// Page 0 holds the NEWEST messages first (the read reverses them), so the note
// goes at its head. Deduped on uuid: the same note arrives again with the next
// history read, and a replayed frame must not print the line twice. Copy-on-
// write (NSAI-5908): only page 0's list is rebuilt, and `oldData` comes back
// untouched when the line is already there.
export const appendMessageToChatHistory = <
  TData extends IChatHistoryPagesContainer,
>({
  oldData,
  message,
}: IAppendMessageToChatHistoryDataProps<TData>): TData => {
  if (hasMessageUuidInPages({ pages: oldData.pages, uuid: message.uuid })) {
    return oldData;
  }

  return updateFirstPageInChatHistory({
    oldData,
    update: (data) => [message, ...data],
  });
};

export const hasMessageUuidInPages = ({
  pages,
  uuid,
}: IHasMessageUuidInPagesProps): boolean => {
  for (const page of pages) {
    for (const message of page.data) {
      if (message.uuid === uuid) return true;
    }
  }
  return false;
};

// NOTE: NSAI-5681 / NSAI-5908 — one copy-on-write step for a connect-card
// frame: merge the card into its message, then, when the resolution wrote a
// transcript line, put that line at the head of the newest page. Shared by
// the foreground hook and the background tracker so both apply the same rules.
export const applyIntegrationConnectPromptToChatHistory = <
  TData extends IChatHistoryPagesContainer,
>({
  oldData,
  payload,
}: IApplyIntegrationConnectPromptToChatHistoryProps<TData>): TData => {
  const withPrompt = updateChatHistoryMessage({
    oldData,
    uuid: payload.uuid,
    mutate: ({ pages }) =>
      applyIntegrationConnectPromptToMessage({
        pages,
        uuid: payload.uuid,
        prompt: payload.prompt,
      }),
  });
  if (!payload.note) {
    return withPrompt;
  }
  return appendMessageToChatHistory({
    oldData: withPrompt,
    message: payload.note,
  });
};

export const upsertNewMessagesIntoChatHistory = (params: {
  oldData: InfiniteData<IChatHistoryPage> | undefined;
  reversedMessages: IMessage[];
  lastMessageUuid: string;
}): InfiniteData<IChatHistoryPage> => {
  const { oldData, reversedMessages, lastMessageUuid } = params;

  if (!oldData) {
    // NOTE: NSAI-7757 — `hasNextPage` must be false, as on mobile (NSAI-4786).
    // This page is built from the messages just received, not from a paginated
    // server response, so no older page is known. Claiming one sent a brand-new
    // chat — shorter than the screen, so the load-older sentinel is in view —
    // straight into `fetchNextPage`: the top loader flashed above the first
    // message and pushed the thread down until the empty page came back. A
    // later history refetch (on mount, or when a new chat's stream finishes)
    // brings the real pagination metadata.
    return {
      pages: [{ data: reversedMessages, hasNextPage: false }],
      pageParams: [0],
    };
  }

  // NOTE: Remove temporary messages (optimistic UI) before adding real ones.
  // NSAI-5908 — only the first page is rebuilt (temps are always prepended
  // there); when there were no temps the reference comes back unchanged.
  const withoutTemporaryMessages =
    removeTemporaryMessagesFromChatHistory(oldData);

  const hasMessage = hasMessageUuidInPages({
    pages: withoutTemporaryMessages.pages,
    uuid: lastMessageUuid,
  });
  // NOTE: If the message already exists, still return the temp-stripped data
  // (not oldData), otherwise stale temp messages would stay in the cache.
  if (hasMessage) return withoutTemporaryMessages;

  return updateFirstPageInChatHistory({
    oldData: withoutTemporaryMessages,
    update: (data) => [...reversedMessages, ...data],
  });
};

// NOTE: NSAI-4601 — re-exported so both call sites (this file's own former
// home for it, and the hook) keep importing from "@/lib/chat-cache-utils".
// The implementation lives in frontend-shared (shared with mobile, and
// generic over any TData shaped like InfiniteData<IChatListPage> without
// frontend-shared depending on @tanstack/react-query) — see
// applyLastMessageAtToChatList in frontend-shared/utils/chat-list-cache.ts.
export { applyLastMessageAtToChatList } from "@frontend-shared/utils";

export const applyToolApprovalRespondedToMessage = (
  params: IApplyToolApprovalRespondedToMessageProps,
): void => {
  const { message, payload } = params;
  if (!message.toolApprovals?.length) return;

  let wasUpdated = false;
  const nextApprovals = [...message.toolApprovals];

  for (let i = 0; i < nextApprovals.length; i += 1) {
    const approval = nextApprovals[i];
    if (approval.toolApproval.approvalId !== payload.approvalId) continue;

    nextApprovals[i] = {
      ...approval,
      toolApproval: {
        ...approval.toolApproval,
        state: payload.approved
          ? EToolApprovalState.APPROVED
          : EToolApprovalState.DENIED,
        approved: payload.approved,
        ...(payload.reason ? { reason: payload.reason } : {}),
      },
    };
    wasUpdated = true;
  }

  if (wasUpdated) {
    message.toolApprovals = nextApprovals;
    // NOTE: NSAI-7877 — an answered approval means the server is resuming this
    // turn, so any interruption stamped while it waited no longer holds. Same
    // rule as a resumed stream in updateMessageInPages above.
    if (!message.done) {
      delete message.streamError;
      message.streamFinished = false;
    }
  }
};

export const upsertToolApprovalRequestToMessage = (
  params: IUpsertToolApprovalRequestToMessageProps,
): void => {
  const { message, payload } = params;

  const nextApproval = {
    tool: { name: payload.toolName, args: payload.args },
    toolApproval: {
      approvalId: payload.approvalId,
      toolCallId: payload.toolCallId,
      state: EToolApprovalState.REQUESTED,
      ...(payload.autoDeclineAfterMinutes
        ? { autoDeclineAfterMinutes: payload.autoDeclineAfterMinutes }
        : {}),
    },
  };

  const nextApprovals = message.toolApprovals ? [...message.toolApprovals] : [];
  for (let i = nextApprovals.length - 1; i >= 0; i -= 1) {
    if (nextApprovals[i].toolApproval.approvalId === payload.approvalId) {
      nextApprovals.splice(i, 1);
    }
  }

  nextApprovals.push(nextApproval);
  message.toolApprovals = nextApprovals;
};

// NOTE: Handles both temporary messages (removes them) and real messages (sets error).
export const applyStreamErrorToData = (
  params: IApplyStreamErrorToDataParams,
): IApplyStreamErrorToDataResult => {
  const { oldData, uuid, errorMessage } = params;

  if (!oldData) {
    return { newData: oldData, shouldShowToast: false };
  }

  // NOTE: If error occurred before message was created on server (uuid is temp or unknown),
  // we remove temporary messages from optimistic UI and show a toast since
  // there is no message to attach the error to.
  if (uuid.startsWith(TEMP_MESSAGE_PREFIX) || uuid === "unknown") {
    return {
      newData: removeTemporaryMessagesFromChatHistory(oldData),
      shouldShowToast: true,
    };
  }

  return {
    newData: updateChatHistoryMessage({
      oldData,
      uuid,
      mutate: ({ pages }) =>
        setMessageStreamError({ pages, uuid, errorMessage }),
    }),
    shouldShowToast: false,
  };
};
